import React from 'react';

import DemoOnboardingTour, { DEMO_ONBOARDING_STUDENT_REPORTS_LANDING_PAGE, } from '../shared/demo_onboarding_tour';
import { getPageTitle, Spinner, SMALL_ICON_BASE_SRC, } from '../../../Shared'
import { requestGet, } from '../../../../modules/request'

const baseImgSrc = `${window.postbuildEnv.CDN_URL}/images/pages/all_reports`

const coreReportCards = [
  {
    title: 'Activity Summary Report',
    href: '/teachers/classrooms/scorebook',
    imgSrc: `${baseImgSrc}/activity_summary.svg`,
    bodyText: 'Quickly see which activities your students have completed and the skills that were demonstrated.',
  }, {
    title: 'Activity Analysis Report',
    href: '/teachers/progress_reports/diagnostic_reports/activity_packs',
    imgSrc: `${baseImgSrc}/activity_analysis.svg`,
    bodyText: 'See how students responded to each question and get a clear analysis of the skills they demonstrated.',
  }
]

const diagnosticReportCards = [
  {
    title: 'My Diagnostics',
    href: '/teachers/progress_reports/diagnostic_reports/diagnostics',
    id: 'my-diagnostics-card',
    imgSrc: `${baseImgSrc}/my_diagnostics.svg`,
    bodyText: 'View results from your assigned diagnostics and get a personalized learning plan with recommended activities.',
  }, {
    title: 'Diagnostic Insights',
    href: '/teachers/progress_reports/diagnostic_insights',
    imgSrc: `${baseImgSrc}/diagnostic_insights.svg`,
    bodyText: 'Track students’ progress over multiple years and get recommendations for which diagnostic to assign next.',
    isNew: true,
  }
]

const premiumReportCards = [
  {
    title: 'Activity Scores Report',
    premium: true,
    href: '/teachers/progress_reports/activities_scores_by_classroom',
    imgSrc: `${baseImgSrc}/activity_scores.svg`,
    bodyText: 'View and download each student’s overall score and their individual scores per activity as a PDF or CSV.',
  }, {
    title: 'Skills Report',
    premium: true,
    href: '/teachers/progress_reports/skills_report',
    imgSrc: `${baseImgSrc}/skills_report.svg`,
    bodyText: 'See the full story of skill growth with complete learning history and actionable groups. Powered by the Momentum Score.',
    isNew: true,
  }, {
    title: 'Standards Report',
    premium: true,
    href: '/teachers/progress_reports/standards_report',
    imgSrc: `${baseImgSrc}/standards_report.svg`,
    bodyText: "Track growth across state standards by combining historical performance with dynamic Smart Groups. Pinpoint exactly where to focus your instruction.",
    isNew: true,
  },{
    title: 'Data Export',
    premium: true,
    href: '/teachers/progress_reports/activity_sessions',
    imgSrc: `${baseImgSrc}/data_export.svg`,
    bodyText: 'You can export the data as a CSV file by filtering for the classrooms, activity packs, or students you would like to export and then pressing “Download Report.”',
  },
]

const contentHubCards = ({ hasAssignedSocialStudiesActivities, hasAssignedWorldHistory1200ToPresentActivities, hasAssignedWorldHistory1750ToPresentActivities, hasAssignedScienceActivities, hasAssignedTeksActivities, hasAssignedFishtankActivities, hasAssignedFishtankGrade7Activities, hasAssignedFishtankGrade8Activities, loading}) => {
  function socialStudiesLink() {
    if (!hasAssignedSocialStudiesActivities) {
      return '/assign/social-studies'
    }

    if (hasAssignedWorldHistory1200ToPresentActivities && !hasAssignedWorldHistory1750ToPresentActivities) {
      return '/teachers/progress_reports/social-studies/world-history-1200-to-present'
    }

    if (!hasAssignedWorldHistory1200ToPresentActivities && hasAssignedWorldHistory1750ToPresentActivities) {
      return '/teachers/progress_reports/social-studies/world-history-1750-to-present'
    }

    return '/teachers/progress_reports/social-studies/'
  }

  const socialStudiesBodyText = hasAssignedSocialStudiesActivities ? 'Your one-stop shop for assigning new social studies activities and tracking student\xa0progress.' : "Explore Quill's Social Studies Activities. Once assigned, return here to assign additional activities and track student\xa0progress."

  const interdisciplinaryScienceLink = hasAssignedScienceActivities ? '/teachers/progress_reports/interdisciplinary-science/building-ai-knowledge' : '/assign/interdisciplinary-science'
  const interdisciplinaryScienceBodyText = hasAssignedScienceActivities ? 'Your one-stop shop for assigning new interdisciplinary science activities and tracking student\xa0progress.' : "Explore Quill's Interdisciplinary Science Activities. Once assigned, return here to assign additional activities and track student\xa0progress."

  const teksBodyText = hasAssignedTeksActivities ? 'Your one-stop shop for assigning TEKS-aligned writing practice tailored to each grade and tracking student progress' : "Explore Quill's TEKS-aligned activities. Once assigned, return here to assign additional activities and track student progress."
  const teksLink = hasAssignedTeksActivities ? '/teachers/progress_reports/quill-for-texas' : '/assign/quill-for-texas'

  const fishtankBodyText = hasAssignedFishtankActivities ? 'Your one-stop shop for assigning writing practice and tracking student progress aligned to Fishtank\xa0ELA' : 'Explore Quill activities aligned to Fishtank ELA. Once assigned, return here to assign additional activities and track student\xa0progress.'

  function fishtankLink() {
    if (!hasAssignedFishtankActivities) { return '/literature' }

    if (hasAssignedFishtankGrade7Activities && !hasAssignedFishtankGrade8Activities) {
      return '/teachers/progress_reports/literature/fishtank-ela-grade-7'
    }

    if (!hasAssignedFishtankGrade7Activities && hasAssignedFishtankGrade8Activities) {
      return '/teachers/progress_reports/literature/fishtank-ela-grade-8'
    }

    return '/teachers/progress_reports/literature'
  }

  return [
    {
      title: 'Literature Dashboard',
      href: fishtankLink(),
      imgSrc: `${baseImgSrc}/literature_dashboard.svg`,
      bodyText: fishtankBodyText,
      isNew: true,
      loading
    }, {
      title: 'Social Studies Dashboard',
      href: socialStudiesLink(),
      imgSrc: `${baseImgSrc}/social_studies_dashboard.svg`,
      bodyText: socialStudiesBodyText,
      loading
    }, {
      title: 'Interdisciplinary Science Dashboard',
      href: interdisciplinaryScienceLink,
      imgSrc: `${baseImgSrc}/interdisciplinary_science_dashboard.svg`,
      bodyText: interdisciplinaryScienceBodyText,
      loading
    }, {
      title: 'Quill for Texas Dashboard',
      href: teksLink,
      imgSrc: `${baseImgSrc}/quill_for_texas_dashboard.svg`,
      bodyText: teksBodyText,
      isNew: true,
      premium: true,
      loading
    },
  ]
}

const Card = ({ isNew, premium, title, id, href, bodyText, imgSrc, loading, }) => {
  const newElement = isNew ? <span className="gold-new-tag">NEW</span> : null
  let linkClassName = 'quill-button focus-on-light contained medium '
  linkClassName += premium ? 'gold' : 'green'
  return (
    <section aria-labelledby={title} className={`report-card ${premium ? 'premium-card' : ''}`} id={id} key={title}>
      {newElement}
      <h3 id={title}>{title}</h3>
      <p>{loading ? <Spinner /> : bodyText}</p>
      <div className="button-and-img-wrapper">
        <a className={linkClassName} href={href}>View Report</a>
        <img alt="" src={imgSrc} />
      </div>
    </section>
  );
};

const ReportSection = ({ cards, imgSrc, title, }) => {
  const reportCardsClassName = `report-cards ${cards.length === 1 ? 'one-card' : ''}`
  return (
    <section aria-labelledby={title} className="report-section">
      <div className="img-and-title-wrapper">
        <img alt="" src={imgSrc} />
        <h2 id={title}>{title}</h2>
      </div>
      <section className={reportCardsClassName}>
        {cards.map(card => <Card key={card.title} {...card} />)}
      </section>
    </section>
  )
}


const LandingPage = ({ flag, }) => {
  document.title = getPageTitle("View Reports", "Landing Page")
  const [loadingContentHubData, setLoadingContentHubData] = React.useState(true)
  const [hasAssignedSocialStudiesActivities, setHasAssignedSocialStudiesActivities] = React.useState(false)
  const [hasAssignedScienceActivities, setHasAssignedScienceActivities] = React.useState(false)
  const [hasAssignedWorldHistory1200ToPresentActivities, setHasAssignedWorldHistory1200ToPresentActivities] = React.useState(false)
  const [hasAssignedWorldHistory1750ToPresentActivities, setHasAssignedWorldHistory1750ToPresentActivities] = React.useState(false)
  const [hasAssignedTeksActivities, setHasAssignedTeksActivities] = React.useState(false)
  const [hasAssignedFishtankActivities, setHasAssignedFishtankActivities] = React.useState(false)
  const [hasAssignedFishtankGrade7Activities, setHasAssignedFishtankGrade7Activities] = React.useState(false)
  const [hasAssignedFishtankGrade8Activities, setHasAssignedFishtankGrade8Activities] = React.useState(false)

  React.useEffect(() => {
    requestGet('/teachers/progress_reports/assigned_content_hub_activities_status',
      ({ has_assigned_science_activities, has_assigned_social_studies_activities, has_assigned_world_history_1200_to_present_activities, has_assigned_world_history_1750_to_present_activities, has_assigned_teks_activities, has_assigned_fishtank_activities, has_assigned_fishtank_ela_grade_7_activities, has_assigned_fishtank_ela_grade_8_activities }) => {
        setHasAssignedScienceActivities(has_assigned_science_activities)
        setHasAssignedSocialStudiesActivities(has_assigned_social_studies_activities)
        setHasAssignedWorldHistory1200ToPresentActivities(has_assigned_world_history_1200_to_present_activities)
        setHasAssignedWorldHistory1750ToPresentActivities(has_assigned_world_history_1750_to_present_activities)
        setHasAssignedTeksActivities(has_assigned_teks_activities)
        setHasAssignedFishtankActivities(has_assigned_fishtank_activities)
        setHasAssignedFishtankGrade7Activities(has_assigned_fishtank_ela_grade_7_activities)
        setHasAssignedFishtankGrade8Activities(has_assigned_fishtank_ela_grade_8_activities)
        setLoadingContentHubData(false)
      }
    )
  })

  return (
    <main className="progress-reports-landing-page">
      <DemoOnboardingTour pageKey={DEMO_ONBOARDING_STUDENT_REPORTS_LANDING_PAGE} />
      <h1>All Reports</h1>
      <ReportSection
        cards={coreReportCards}
        imgSrc={`${SMALL_ICON_BASE_SRC}/bar-chart.svg`}
        title='Core Reports'
      />
      <ReportSection
        cards={diagnosticReportCards}
        imgSrc={`${SMALL_ICON_BASE_SRC}/bar-graph-increasing.svg`}
        title='Diagnostic Reports'
      />
      <ReportSection
        cards={premiumReportCards}
        imgSrc={`${SMALL_ICON_BASE_SRC}/premium-diamond.svg`}
        title='Premium Reports'
      />
      <ReportSection
        cards={contentHubCards({ hasAssignedSocialStudiesActivities, hasAssignedWorldHistory1200ToPresentActivities, hasAssignedWorldHistory1750ToPresentActivities, hasAssignedScienceActivities, hasAssignedTeksActivities, hasAssignedFishtankActivities, hasAssignedFishtankGrade7Activities, hasAssignedFishtankGrade8Activities, loadingContentHubData, })}
        imgSrc={`${SMALL_ICON_BASE_SRC}/graduation-cap.svg`}
        title='Curriculum-Specific Dashboards'
      />
    </main>
  );
}

export default LandingPage
